import { useMutation, useQuery } from '@apollo/client'
import { Icon } from '@iconify/react'
import inboxOutline from '@iconify/icons-eva/inbox-outline'
import {
  Badge, Box, Button, IconButton, Popover
} from '@mui/material'
import { formatDistanceToNow, parseISO } from 'date-fns'
import { TFunction } from 'i18next'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useHistory } from 'react-router'
import DateUtil from '../../util/DateUtil'
import { TOKENS } from '../dashboard-v2/components/primitives'
import { INBOX, MARK_NOTIFICATIONS_READ, V2Notification } from './graphql'

// "Lena M. hat dich erwähnt": what happened, in words
export function notificationText(t: TFunction, item: V2Notification): string {
  const name = item.actorName || t('boardV2.inbox.someone')
  return t(`boardV2.inbox.kinds.${item.kind || 'other'}`, { name, text: item.text, defaultValue: item.title })
}

const PAGE = 30
const MAX_ITEMS = 300

// a link of the app opens in the app, anything else in the browser
export function openUrl(url: string, push: (path: string) => void) {
  try {
    const target = new URL(url, window.location.origin)
    const sameApp = target.origin === window.location.origin || target.hostname === 'app.alphaprocess.io'
    if (sameApp) push(`${target.pathname}${target.search}${target.hash}`)
    else window.open(target.href, '_blank', 'noopener')
  } catch {
    // not a link: nothing to open
  }
}

// the inbox ("Benachrichtigungen"): what happened on cards, and every notification sent as a
// push, also without a device, as an archive; a badge with the unread count, the list in a
// popover, a click opens the card or the page of the notification and marks the entry read
export default function InboxButton({ size = 40, className }: { size?: number, className?: string }) {
  const { t } = useTranslation()
  const history = useHistory()
  const [anchor, setAnchor] = useState<HTMLElement | null>(null)
  const [limit, setLimit] = useState(PAGE)
  const { data, refetch } = useQuery<{ inbox: { unreadCount: number, items: V2Notification[] } }>(INBOX, {
    variables: { limit }, pollInterval: 60000, fetchPolicy: 'cache-and-network', errorPolicy: 'ignore'
  })
  const [markRead] = useMutation(MARK_NOTIFICATIONS_READ)
  const unread = data?.inbox.unreadCount || 0
  const items = data?.inbox.items || []
  const when = (iso: string) => formatDistanceToNow(parseISO(iso), { addSuffix: true, locale: DateUtil.getLocale() })

  const open = (item: V2Notification) => {
    setAnchor(null)
    if (!item.readAt) markRead({ variables: { ids: [item.id] } }).then(() => refetch()).catch(() => undefined)
    if (item.boardId && item.taskId) history.push(`/boards/${item.boardId}?task=${item.taskId}`)
    else if (item.url) openUrl(item.url, history.push)
  }

  const readAll = () => markRead({ variables: { all: true } }).then(() => refetch()).catch(() => undefined)

  return (
    <>
      <IconButton
        className={className}
        aria-label={t('boardV2.inbox.open', { count: unread })}
        onClick={(event) => { setAnchor(event.currentTarget); refetch() }}
        sx={{ width: size, height: size, color: TOKENS.text }}
      >
        <Badge badgeContent={unread} color="error" max={99}>
          <Icon icon={inboxOutline} width={22} height={22} />
        </Badge>
      </IconButton>
      <Popover
        open={!!anchor}
        anchorEl={anchor}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{ sx: { width: 380, maxWidth: 'calc(100vw - 32px)', maxHeight: '70vh', display: 'flex', flexDirection: 'column' } }}
      >
        <Box sx={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 2, py: 1.25, borderBottom: `1px solid ${TOKENS.divider}`
        }}
        >
          <Box component="h2" sx={{ m: 0, fontSize: 16, fontWeight: 650 }}>{t('boardV2.inbox.title')}</Box>
          {unread > 0 && <Button size="small" onClick={readAll} sx={{ whiteSpace: 'nowrap', flex: '0 0 auto' }}>{t('boardV2.inbox.readAll')}</Button>}
        </Box>
        <Box component="ul" sx={{ listStyle: 'none', m: 0, p: 0, overflowY: 'auto' }}>
          {items.length === 0 && (
            <Box component="li" sx={{ px: 2, py: 3, fontSize: 14, color: TOKENS.muted }}>{t('boardV2.inbox.empty')}</Box>
          )}
          {items.map((item) => (
            <Box component="li" key={item.id} sx={{ borderBottom: `1px solid ${TOKENS.divider}` }}>
              <Box
                component="button"
                type="button"
                onClick={() => open(item)}
                sx={{
                  display: 'flex', gap: 1.25, width: '100%', textAlign: 'left', border: 0, cursor: 'pointer', px: 2, py: 1.25, font: 'inherit', color: TOKENS.text,
                  bgcolor: item.readAt ? 'transparent' : '#EEF5FD', '&:hover': { bgcolor: '#F2F4F7' }
                }}
              >
                <Box
                  component="span"
                  aria-hidden
                  sx={{
                    width: 8, height: 8, flex: '0 0 8px', mt: 0.75, borderRadius: '50%', bgcolor: item.readAt ? 'transparent' : TOKENS.primary
                  }}
                />
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  {item.kind !== 'push' && <Box sx={{ fontSize: 13 }}>{notificationText(t, item)}</Box>}
                  <Box sx={{ fontSize: 14, fontWeight: 600, mt: item.kind === 'push' ? 0 : 0.25, overflowWrap: 'anywhere' }}>{item.title}</Box>
                  {(item.kind === 'comment' || item.kind === 'mention' || item.kind === 'push') && item.text && item.text !== item.title && (
                    <Box sx={{
                      fontSize: 13, color: TOKENS.muted, mt: 0.25, overflow: 'hidden', textOverflow: 'ellipsis', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical'
                    }}
                    >
                      {item.text}
                    </Box>
                  )}
                  <Box sx={{ fontSize: 12, color: TOKENS.muted, mt: 0.5 }}>{when(item.createdAt)}</Box>
                </Box>
              </Box>
            </Box>
          ))}
          {items.length >= limit && limit < MAX_ITEMS && (
            <Box component="li" sx={{ p: 1, textAlign: 'center' }}>
              <Button size="small" onClick={() => setLimit((x) => Math.min(x + PAGE, MAX_ITEMS))}>{t('boardV2.inbox.older')}</Button>
            </Box>
          )}
        </Box>
      </Popover>
    </>
  )
}
